import React, { useState, useEffect } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import useBroker from '@hooks/useBroker'
import useSymbolNames from '@hooks/useSymbolNames'
import { LIVE_RUN_MODES } from '@util/wallet'

// 이 페이지가 구독할 것. **컴포넌트 밖**에 둔다 -- 안에 인라인 배열로 두면 매 렌더 새 참조가
// 되어 훅이 접속을 다시 맺을 여지가 생긴다(WalletPage와 같은 이유).
//
// topic이 'signal'인데 실제 토픽명은 'admin'이다 -- 그 매핑은 서버(BrokerController)가
// topicNames로 내려주고 훅이 그대로 구독한다. 여기서 'admin'이라고 쓰면 안 된다.
//
// idField가 **tableId**다(`symbolId/runMode`): 표는 심볼마다 한 장이 아니라
// **심볼 x run_mode마다 한 장**이다. 유저 축은 없다(신호는 계좌가 아니라 심볼/전략의 사실이라
// slot에서 한 신호가 여러 계좌로 방송된다).
//
// ⚠ 예전엔 `symbolId`였고, 그래서 같은 심볼을 MAINNET·TESTNET이 각자 돌 때 뒤에 온 장이 앞의
// 것을 **통째로 덮어썼다**(훅이 스냅샷을 병합하지 않는다) -- MAINNET의 1h/4h/1d가 30m 한 줄로
// 사라졌다. 조립은 트레이더가 하고(`SignalTable.tableId`) 여기선 그 문자열을 쓰기만 한다.
const SUBSCRIPTIONS = [
  { topic: 'signal', service: 'trader.signal', idField: 'tableId' },
]

// 트레이더 발행이 **분 경계마다 1회**다(LiveRunner 폴링 회전당 1회). 캔들이 하나도 안 온
// 회전에도 uptAt만 갱신해 쏘므로 이 값은 heartbeat 주기이기도 하다.
// 3분을 주는 이유: 1분 경계 + 파이프라인 지연(PollOffsetMs 10초)이라 2분은 정상 지터에 걸린다.
const STALE_MILLIS = 180000

// uptAt은 스냅샷이 와야 바뀐다. 트레이더가 죽으면 아무것도 안 오므로 상태 변화도 리렌더도
// 없어 **[발행 멈춤]이 영영 안 뜬다.** 시계를 돌려 주기적으로 다시 판정한다(WalletPage와 동일).
const TICK_MILLIS = 10000

/** 열 순서 -- **지금 도는 배선**의 전략을 적힌 순서대로.
 *
 * 확정본이 `strategies=4h:shell,1h:boost` 라 둘이다(4h 껍질이 바탕, 1h 얹기가 그 위).
 *
 * ⚠ [2026-08-27 주인 지시] 옛 목록은 `['drift', 'flip', 'swing']` 이었고, 주석은 트레이더의
 *   `Strategies.canonicalOrder` 와 같아야 한다고 적고 있었다. **그 기준이 낡았다** --
 *   `canonicalOrder` 는 `strategies=default` 별칭의 표준 조합이고, 확정본은 그 별칭을 안 쓴다.
 *   그래서 화면에 **영영 비는 열 셋**이 서고, 실제로 도는 둘은 "모르는 전략" 취급으로 뒤에
 *   덧붙어 있었다. 기준을 "canonical 의 사본" 에서 **"지금 도는 배선"** 으로 바꾼다.
 *
 * ⚠ 트레이더의 `canonicalOrder` 는 **안 건드렸다** -- 그건 백테 `strategies=default` 의 뜻이라
 *   손대면 지문이 갈린다. 여기는 화면 열 순서일 뿐이다.
 *
 * 어긋나도 칸이 사라지지는 않는다 -- 아래 `columnsOf`가 이 목록에 없는 전략을 **뒤에 덧붙인다.**
 * 그래야 배선을 바꿔 옛 전략을 다시 돌렸을 때 그 신호가 화면에서 조용히 빠지지 않는다.
 * 목록의 역할은 "순서 고정"과 "아직 신호가 없는 전략도 빈 칸으로 보이게 하는 것"뿐이다. */
const STRATEGIES = ['shell', 'boost']

/** 행 순서 -- 짧은 tf부터. 트레이더 `Timeframes.all`의 순서와 같다.
 *
 * 문자열 정렬을 쓰면 안 되는 이유: '1d' < '1h' < '30m' < '4h'가 되어 표가 뒤죽박죽이 된다
 * (서버가 보내는 signals 배열은 tf 문자열 오름차순으로 정렬돼 있다 -- 그대로 쓰면 이 꼴이다). */
const TF_ORDER = ['5m', '15m', '30m', '1h', '4h', '1d']

/** 캔들 openTime은 거래소 축이고 트레이더 로그(`StrategyEngine.dateTimeOf`)도 UTC로 찍는다.
 * 화면만 KST로 그리면 로그와 대조할 때 9시간이 어긋난다 -- 대조가 이 표의 주 용도이므로 UTC로 둔다. */
const utcTextOf = (millis) => {
  const pad = (n) => String(n).padStart(2, '0')
  const d = new Date(millis)
  const date = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  return `${date} ${time}`
}

/** 절대 시각만 있으면 "이게 오래된 건가"를 매번 암산해야 한다. 1d 전략은 신호가 몇 주에 한 번이라
 * 그 판단이 이 표의 핵심이다. */
const ageTextOf = (millis) => {
  const elapsed = Date.now() - millis
  if (elapsed < 0) {
    return '방금'
  }
  const minutes = Math.floor(elapsed / 60000)
  if (minutes < 60) {
    return `${minutes}분 전`
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}시간 전`
  }
  const days = Math.floor(hours / 24)
  return `${days}일 전`
}

/** 자릿수를 값 크기로 가른다 -- BTC(10만대)와 알트(0.0001대)가 같은 표에 뜰 수 있어
 * 고정 자릿수로는 한쪽이 반드시 못 읽는 모양이 된다.
 *
 * 유한수 검사를 두는 이유: `rawStopPrice`는 원래 NaN이 될 수 있는 자리다(`RiskMath.flooredStopPrice`가
 * NaN을 "스탑 없는 진입"으로 통과시킨다). 지금 경로에서는 안 오지만, 오면 화면에 'NaN'이 아니라
 * '-'가 떠야 한다. */
const priceTextOf = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-'
  }
  const digits = Math.abs(value) >= 100 ? 2 : (Math.abs(value) >= 1 ? 4 : 8)
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

/** [신호 상태, 2026-08-31 주인 지시] 칸의 두 번째 줄 -- **그 신호가 아직 살아 있는가.**
 *
 * 진입 신호만 있던 동안에는 "3ATR로 마감까지 된 건지 현재까지 유효한 건지" 표만 보고는
 * 알 수 없었다. 트레이더가 `state`/`heldSince`/`lastExitAt`을 같이 실어 준다
 * (트레이더 `SignalHold` 머리글).
 *
 * ⚠ **`state`가 없으면 아무 것도 안 그린다.** null은 "청산됨"이 아니라 **"이 규칙은 상태를
 * 안 밝힌다"**다 -- 죽은 것으로 그리면 상태를 구현 안 한 규칙의 살아 있는 신호가 전부
 * 사라진 것처럼 보인다. 모르는 값도 같은 이유로 그냥 안 그린다.
 *
 * 시각이 0이면 "없음"이다(트레이더가 0을 그 뜻으로 쓴다 -- anchorOpenTime과 같은 규약). */
const holdTextOf = (signal) => {
  if (signal.state === 'HOLDING') {
    const since = signal.heldSince > 0 ? ` · ${ageTextOf(signal.heldSince)} 진입` : ''
    return { text: `보유중${since}`, color: 'green' }
  }
  if (signal.state === 'CLOSED') {
    const at = signal.lastExitAt > 0
      ? `${utcTextOf(signal.lastExitAt)} · ${ageTextOf(signal.lastExitAt)}`
      : '시각 없음'
    return { text: `청산 ${at}`, color: '#888' }
  }
  return null
}

/** 모르는 tf는 **뒤로 보내되 버리지 않는다.** 걸러내면 웹의 목록이 낡았을 때 그 tf 행이
 * 화면에서 조용히 사라진다 -- 그게 관측 화면에서 제일 나쁜 실패다. */
const tfIndexOf = (tf) => {
  const index = TF_ORDER.indexOf(tf)
  return index === -1 ? TF_ORDER.length : index
}

/** 이 심볼 표의 열 목록 = canonical 순서 + 목록에 없던 전략(온 순서대로 뒤에). */
const columnsOf = (signals) => {
  const extras = []
  signals.forEach((signal) => {
    const known = STRATEGIES.indexOf(signal.strategy) !== -1
    const already = extras.indexOf(signal.strategy) !== -1
    if (!known && !already) {
      extras.push(signal.strategy)
    }
  })
  return STRATEGIES.concat(extras)
}

/** signals 배열(평평한 목록)을 `표[tf][strategy]` 격자로 접는다. */
const gridOf = (signals) => {
  const byTf = {}
  signals.forEach((signal) => {
    if (!byTf[signal.tf]) {
      byTf[signal.tf] = {}
    }
    byTf[signal.tf][signal.strategy] = signal
  })
  return byTf
}

const SignalPage = () => {
  const { records, services, connected } = useBroker(SUBSCRIPTIONS)
  // 이름 사전. **표에는 안 실려 온다** -- 출처를 DB 하나로 두는 근거는 `SymbolController` 머리글.
  const { symbolTextOf } = useSymbolNames()
  const [, set_tick] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => set_tick(n => n + 1), TICK_MILLIS)
    return () => clearInterval(timer)
  }, [])

  // records.signal은 {'1/MAINNET': 표, '1/TESTNET': 표} 모양이라 값만 뽑아 편다.
  //
  // **드릴은 뺀다.** 목록 화면과 같은 선이다(`LIVE_RUN_MODES`) -- 드릴 프로세스는 상시 가동이
  // 아니라 대개 [발행 멈춤]로만 줄줄이 떠서 진짜 멈춤을 눈에 안 띄게 만든다. 운영 화면에
  // 시험용 표가 섞이면 실거래 표가 그 안에 묻힌다.
  //
  // 정렬을 두는 이유: 맵 순서는 mqtt 도착 순서라 갱신마다 표 순서가 바뀐다. 심볼을 먼저,
  // 같은 심볼 안에서는 `LIVE_RUN_MODES` 순(MAINNET 먼저)으로 -- 문자열 정렬을 쓰면
  // MAINNET < TESTNET이 우연히 맞을 뿐이고 모드가 늘면 어긋난다.
  const tables = Object.values(records.signal)
    .filter((t) => LIVE_RUN_MODES.includes(t.runMode))
    .sort((a, b) => (a.symbolId - b.symbolId)
      || (LIVE_RUN_MODES.indexOf(a.runMode) - LIVE_RUN_MODES.indexOf(b.runMode)))
  const signalService = services['trader.signal']

  // ⚠ 표가 갈리면서 이 판정이 **프로세스별**이 됐다. 전에는 한쪽이 죽어도 산 쪽이 같은 칸을
  // 덮어써 [발행 멈춤]이 영영 안 떴다 -- 이제 "TESTNET만 멎었다"가 화면에 드러난다.
  const isStale = (table) => {
    return Date.now() - table.uptAt > STALE_MILLIS
  }

  const renderCell = (signal, tf, strategy) => {
    const key = `${tf}-${strategy}`

    // 빈 칸 = "그 조합에서 아직 신호가 없었다". 사실이므로 그대로 그린다 --
    // 이 칸이 비어 있는 것과 발행이 멈춘 것은 다른 사건이고, 후자는 헤더의 [발행 멈춤]이 말한다.
    if (!signal) {
      return <TableCell key={key} style={{ color: '#ccc' }}>-</TableCell>
    }

    const isLong = signal.side === 'LONG'

    // 툴팁에 미는 값들: 격자에 다 넣으면 칸이 넘쳐 tf x strategy가 한눈에 안 들어온다.
    //
    // **rawStopPrice를 '손절가'라고 쓰지 않는다.** 이 값은 전략이 낸 원본이고 ATR floor를
    // 아직 안 통과했다(확정값 stopPrice는 발주 시점에 체결가·ATR로 유도되므로 신호 관측
    // 시점엔 존재하지 않는다). '손절가'라고 부르면 화면이 실제 손절선과 다른 숫자를 그 이름으로
    // 보여주게 된다 -- 확정 손절은 live_orders.stop_price를 봐야 하고 이 표의 관심사가 아니다.
    // 보유/청산 시각은 **tf 봉 축**이고 신호 캔들은 5분봉 축이라 서로 몇 분 어긋난다
    // (트레이더 `SignalHold` 머리글). 툴팁에 축을 적어 두지 않으면 로그와 맞춰볼 때 헷갈린다.
    const hold = holdTextOf(signal)
    const detail = [
      `신호 캔들 ${utcTextOf(signal.at)} UTC`,
      `앵커 ${signal.anchorOpenTime > 0 ? `${utcTextOf(signal.anchorOpenTime)} UTC` : '없음'}`,
      `rawStopPrice ${priceTextOf(signal.rawStopPrice)} (전략 원본, ATR floor 전)`,
      hold && `보유 시작 ${signal.heldSince > 0 ? `${utcTextOf(signal.heldSince)} UTC` : '없음'} (tf봉 시작 시각)`,
      hold && `마지막 청산 ${signal.lastExitAt > 0 ? `${utcTextOf(signal.lastExitAt)} UTC` : '없음'} (tf봉 시작 시각)`,
      hold && '⚠ 규칙 모형의 지갑이다 -- 실제 체결/포지션은 지갑 화면을 본다',
    ].filter(Boolean).join('\n')

    return (
      <TableCell key={key}>
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detail}</span>}>
          <span>
            <b style={{ color: isLong ? 'green' : 'red' }}>{signal.side}</b>
            {' '}{priceTextOf(signal.price)}
            <div style={{ color: '#888' }}>
              {utcTextOf(signal.at)} · {ageTextOf(signal.at)}
            </div>
            {hold && <div style={{ color: hold.color }}>{hold.text}</div>}
          </span>
        </Tooltip>
      </TableCell>
    )
  }

  const renderTable = (table) => {
    const signals = table.signals || []
    const columns = columnsOf(signals)
    const byTf = gridOf(signals)

    // 행은 **온 tf만** 그린다 -- 어느 tf가 돌고 있는지의 진실은 `wallets` 테이블이고 이 payload에
    // 없다. TF_ORDER 전부를 행으로 깔면 지갑이 없는 tf까지 빈 칸으로 그려서 "신호가 없다"와
    // "그 tf가 안 돈다"가 한 모양이 된다. 그 구별은 트레이더가 표에 tfs를 실어줘야 생긴다.
    //
    // ⚠ 이 목록이 **그 프로세스가 맡은 tf**라는 걸 헤더의 모드가 말해줘야 한다. 모드 표기가
    // 없으면 TESTNET 표(30m 한 줄)가 "MAINNET에서 1h/4h/1d 신호가 없어졌다"로 읽힌다.
    const tfs = Object.keys(byTf).sort((a, b) => tfIndexOf(a) - tfIndexOf(b))

    return (
      // key가 **tableId**다. `symbolId`로 두면 같은 심볼의 두 모드가 같은 key를 갖고, React가
      // 한 블록에 두 표를 밀어 넣는다 -- 이 화면에서 신호가 사라져 보였던 사고를 화면 쪽에서
      // 한 번 더 되풀이하는 자리다.
      <div key={table.tableId} style={{ marginBottom: 24 }}>
        <div>
          {/* [이름 붙이기, 2026-08-27 주인 지시] `symbolId 1` -> `# 1 (BTCUSDT)`.
              사전을 못 받았으면 `# 1` 로 돌아간다 -- 숫자는 어느 쪽이든 맞다. */}
          <b>{symbolTextOf(table.symbolId)}</b>
          {/* 모드는 심볼 바로 옆이다 -- 이게 없으면 블록 둘이 똑같아 보인다. */}
          <span> · {table.runMode}</span>
          {table.exchangeId && <span> · {table.exchangeId}</span>}
          <span style={{ color: '#888' }}> · 발행 {utcTextOf(table.uptAt)} UTC</span>
          {isStale(table) && <span style={{ color: 'red' }}> [발행 멈춤]</span>}
        </div>

        {tfs.length === 0
          ? <div style={{ color: '#888' }}>신호 없음 -- 웜업 중이거나 아직 한 건도 안 떴다</div>
          : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>tf</TableCell>
                  {columns.map(strategy => <TableCell key={strategy}>{strategy}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {tfs.map(tf => (
                  <TableRow key={tf}>
                    <TableCell><b>{tf}</b></TableCell>
                    {columns.map(strategy => renderCell(byTf[tf][strategy], tf, strategy))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </div>
    )
  }

  return (
    <>
      {!connected && <div style={{ color: 'red' }}>브로커 연결 끊김</div>}
      {signalService && signalService.error && <div style={{ color: 'red' }}>트레이더 오류: {signalService.error}</div>}
      {tables.length === 0 && <div>신호 표 없음 -- 트레이더 미가동이거나 웜업이 아직 안 끝났다</div>}
      {tables.map(renderTable)}
    </>
  )
}

export default SignalPage
